import { CommonModule } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import 'simple-keyboard-layouts/build/layouts/english';
import Keyboard, { KeyboardOptions } from 'simple-keyboard';

import { DealerServiceToken } from '../../factories/dealer.factory';
import { WorkerAccessLevel } from '../../models/communication/app-enums.model';
import { WorkerRead } from '../../models/communication/app-events.model';
import { DealerService } from '../../services/dealer.service';

@Component({
    selector: 'app-register-worker',
    imports: [CommonModule, FormsModule],
    templateUrl: './register-worker.component.html',
    styleUrls: ['./register-worker.component.scss']
})
export class RegisterWorkerComponent implements OnInit, OnChanges {
    @Input()
    workerData: WorkerRead | null = null;
    @Input() tableGroups: string[] = [];

    @Output() readonly closeClicked = new EventEmitter<void>();
    @Output() readonly backClicked = new EventEmitter<void>();
    @Output() readonly cancelSupervisorAction = new EventEmitter<void>();

    @ViewChild('keyboardContainer', { static: false })
    keyboardContainer?: ElementRef<HTMLDivElement>;

    @ViewChild('nameInput', { static: false })
    nameInput?: ElementRef<HTMLInputElement>;

    @ViewChild('tableGroupDropdown', { static: false })
    tableGroupDropdown?: ElementRef<HTMLDivElement>;

    @ViewChild('roleDropdown', { static: false })
    roleDropdown?: ElementRef<HTMLDivElement>;

    step = 1;
    userId: string | null = null;
    user: { name: string; accessLevel: string; tableGroups: string[] } = {
        name: '',
        accessLevel: '',
        tableGroups: []
    };
    keyboard: Keyboard | null = null;
    activeInput: 'name' | null = null;
    tableGroupSelection: Record<string, boolean> = {};
    roles: string[] = [$localize`Supervisor`, $localize`Croupier`];
    tableGroupDropdownOpen = false;
    roleDropdownOpen = false;

    constructor(@Inject(DealerServiceToken) private readonly dealerService: DealerService) {}

    get tableGroupDisplay(): string {
        if (this.user.tableGroups.length === 0) {
            return 'TABLE GROUP';
        }
        if (this.user.tableGroups.length === 1) {
            return this.user.tableGroups[0];
        }
        return `${this.user.tableGroups.length} groups selected`;
    }

    get isFormValid(): boolean {
        return !!this.user.name && this.user.tableGroups.length > 0 && !!this.user.accessLevel;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        if (
            this.tableGroupDropdownOpen &&
            this.tableGroupDropdown?.nativeElement &&
            !this.tableGroupDropdown.nativeElement.contains(target)
        ) {
            this.tableGroupDropdownOpen = false;
        }

        if (this.roleDropdownOpen && this.roleDropdown?.nativeElement && !this.roleDropdown.nativeElement.contains(target)) {
            this.roleDropdownOpen = false;
        }

        const clickedInput = this.nameInput?.nativeElement.contains(target);
        const clickedKeyboard = this.keyboardContainer?.nativeElement.contains(target);

        if (this.keyboard && !clickedInput && !clickedKeyboard) {
            this.hideKeyboard();
        }
    }

    ngOnInit(): void {
        this.updateTableGroupSelection();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('workerData' in changes && this.workerData) {
            this.userId = this.workerData.id;
            this.user = {
                name: this.workerData.name ?? '',
                accessLevel: this.workerData.accessLevel ?? '',
                tableGroups: this.workerData.tableGroups ?? []
            };
            this.updateTableGroupSelection();

            this.step = 2;
        } else {
            this.step = 1;
        }
    }

    onInputFocus(): void {
        if (!this.keyboard && this.keyboardContainer?.nativeElement) {
            const options: KeyboardOptions = {
                layoutName: 'default',
                onChange: (input: string) => (this.user.name = input),
                onKeyPress: (button: string) => this.onKeyPress(button)
            };

            this.keyboard = new Keyboard(this.keyboardContainer.nativeElement, options);
        }

        this.keyboard?.setInput(this.user.name);
    }

    onKeyboardInput(input: string): void {
        if (this.activeInput) {
            this.user[this.activeInput] = input;
        }
    }

    onKeyPress(button: string): void {
        if (!this.keyboard) {
            return;
        }

        if (button === '{shift}' || button === '{lock}') {
            const currentLayout = this.keyboard.options.layoutName ?? 'default';
            const nextLayout = currentLayout === 'default' ? 'shift' : 'default';
            this.keyboard.setOptions({ layoutName: nextLayout });
        }

        if (button === '{enter}') {
            this.hideKeyboard();
        }
    }

    hideKeyboard(): void {
        if (!this.keyboardContainer) {
            return;
        }

        if (this.keyboard) {
            this.keyboard.destroy();
            this.keyboard = null;
        }

        this.keyboardContainer.nativeElement.innerHTML = '';
        this.activeInput = null;
    }

    goBack(): void {
        if (this.step > 1) {
            this.step--;
        }
        this.backClicked.emit();
    }

    editUser(): void {
        this.step = 2;
    }

    async submitForm(): Promise<void> {
        if (!this.userId || !this.user.name || this.user.tableGroups.length === 0 || !this.user.accessLevel) {
            return;
        }

        try {
            const response = await this.dealerService.sendRegisterWorkerRequest({
                id: this.userId,
                name: this.user.name,
                tableGroups: this.user.tableGroups,
                accessLevel: this.user.accessLevel as WorkerAccessLevel
            });

            if (response.error) {
                this.step = 1;
            } else {
                this.step = 3;
            }
        } catch {
            // Handle network or other errors
            // this.errorOccurred.emit('Failed to register worker');
        }
    }

    toggleTableGroupDropdown(): void {
        this.tableGroupDropdownOpen = !this.tableGroupDropdownOpen;
        if (this.tableGroupDropdownOpen) {
            this.roleDropdownOpen = false;
        }
    }

    toggleRoleDropdown(): void {
        this.roleDropdownOpen = !this.roleDropdownOpen;
        if (this.roleDropdownOpen) {
            this.tableGroupDropdownOpen = false;
        }
    }

    toggleTableGroup(group: string): void {
        const index = this.user.tableGroups.indexOf(group);
        if (index > -1) {
            this.user.tableGroups.splice(index, 1);
            this.tableGroupSelection[group] = false;
        } else {
            this.user.tableGroups.push(group);
            this.tableGroupSelection[group] = true;
        }
    }

    selectRole(role: string): void {
        this.user.accessLevel = role;
        this.roleDropdownOpen = false;
    }

    trackByGroup = (_: number, group: string): string => group;

    trackByRole = (_: number, role: string): string => role;

    private updateTableGroupSelection(): void {
        this.tableGroupSelection = {};
        this.tableGroups.forEach((group) => {
            this.tableGroupSelection[group] = this.user.tableGroups.includes(group);
        });
    }
}
